import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload, FaTrash, FaCircleCheck } from 'react-icons/fa6';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import Button from '../../../shared/components/ui/Button';
import CooperationLoader from '../../../shared/components/feedback/CooperationLoader';
import { RegistrationApiService } from '../api/RegistrationApiService';
import { useStepRedirect, skipNextStepRedirect } from '../../../hooks/useStepRedirect';
import { normalizeAttachments } from '../../../shared/utils/attachmentUtils';
import { useDoctorInfoWithAutoRefresh } from '../contexts/DoctorInfoContext';
import upload_icon from '../../../assets/upload.svg';
import WebcamCapture from '../../../shared/components/ui/WebcamCapture';
import ImageCropper from '../../../shared/components/ui/ImageCropper';
import ClinicRoomsUpload, { RoomDocumentState } from '../../../shared/components/ui/ClinicRoomsUpload';
import { Ministry } from '../../../shared/types/models/Ministries';
import { DoctorType, deriveDoctorType } from '../../../shared/utils/doctorType';
import { hasPendingFiles, useUnsavedChanges } from "../../../shared/utils/unsavedChanges";
import { ACCEPT_IMAGES_AND_PDF, checkUploadFile, isHeic, isPdfFile, uploadProblemKey } from "../../../shared/utils/uploadPolicy";

// Aspect ratios for each document type (width / height).
// Syndicate ID is a CR80 card (landscape ~1.586). Certificates/proof are A4-ish portrait.
const ID_CARD_ASPECT = 1.586;
const A4_PORTRAIT_ASPECT = 0.707;

type DocSetter = React.Dispatch<React.SetStateAction<DocumentFile>>;

interface PendingCapture {
  setter: DocSetter;
  aspect?: number;
  fileNameBase: string;
  inputId: string;
  allowFlip?: boolean;
}

interface CropperState {
  imageSrc: string;
  fileName: string;
  aspect?: number;
  source: 'camera' | 'file';
}

interface DoctorDocumentsScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

interface DocumentFile {
  file: File | null;
  preview: string | null;
  uploading: boolean;
  uploaded: boolean;
  error: string | null;
  existingUrl?: string; // URL from API for already uploaded documents
}

const DoctorDocumentsScreen: React.FC<DoctorDocumentsScreenProps> = ({ onNext, onBack, onError }) => {
  useStepRedirect();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { doctorInfo, refreshDoctorInfo } = useDoctorInfoWithAutoRefresh();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const registrationApi = new RegistrationApiService();

  // Document states
  const [syndicateIdFront, setSyndicateIdFront] = useState<DocumentFile>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
  });

  const [syndicateIdBack, setSyndicateIdBack] = useState<DocumentFile>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
  });

  const [specializationCert, setSpecializationCert] = useState<DocumentFile>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
  });

  const [proofOfPractice, setProofOfPractice] = useState<DocumentFile>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
  });

  // Iraqi certificate-equivalency form — required when the doctor graduated abroad ("Other")
  // امر اداري الخاص بالدبلوم العالي — stands in for the specialization certificate
  // when the doctor registered as دبلوم عالي.
  const [higherDiplomaOrder, setHigherDiplomaOrder] = useState<DocumentFile>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
  });

  const [equivalencyCert, setEquivalencyCert] = useState<DocumentFile>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
  });

  const [otherAttachments, setOtherAttachments] = useState<RoomDocumentState[]>([]);

  // Extra documents required only for doctors who came via the IMA ID QR fallback
  // (DMIS unavailable): residence card (f/b), doctor photo, civil/unified ID (f/b).
  const emptyDoc = (): DocumentFile => ({ file: null, preview: null, uploading: false, uploaded: false, error: null });
  const [residenceCardFront, setResidenceCardFront] = useState<DocumentFile>(emptyDoc());
  const [residenceCardBack, setResidenceCardBack] = useState<DocumentFile>(emptyDoc());
  const [doctorPhoto, setDoctorPhoto] = useState<DocumentFile>(emptyDoc());
  const [civilIdFront, setCivilIdFront] = useState<DocumentFile>(emptyDoc());
  const [civilIdBack, setCivilIdBack] = useState<DocumentFile>(emptyDoc());
  const [graduationCert, setGraduationCert] = useState<DocumentFile>(emptyDoc());
  // Files picked on this device but not uploaded yet: ask before a design switch discards them.
  useUnsavedChanges(hasPendingFiles(syndicateIdFront, syndicateIdBack, specializationCert, proofOfPractice, higherDiplomaOrder, equivalencyCert, residenceCardFront, residenceCardBack, doctorPhoto, civilIdFront, civilIdBack, graduationCert, otherAttachments));

  const [doctorType, setDoctorType] = useState<DoctorType>('');
  const isPractitioner = doctorType === 'practitioner';
  const isHigherDiploma = doctorType === 'higher_diploma';
  // Everyone else (including profiles saved before نوع الطبيب was stored, which read
  // back as "specialist") hands in the specialization certificate.
  const needsSpecializationCert = !isPractitioner && !isHigherDiploma;
  // QR-fallback doctors (identity came from the IMA ID QR, not DMIS) must provide the
  // extra identity documents below.
  const identitySource = doctorInfo?.doctor_info?.identity_source;
  const isQrDoctor = identitySource === 'ima_qr' || identitySource === 'manual_qr_fallback';
  // Derived from the (refreshed) profile so it always reflects the current choice —
  // shows the equivalency upload when the doctor picked "Other", and hides it again
  // if they switch back to a real university.
  const isOtherUniversity = !!(doctorInfo?.doctor_info?.graduated_from_other);
  // Ministry of Health doctors must supply the workplace confirmation letter itself;
  // everyone else may hand in their syndicate ID instead, so only they get the
  // "or the syndicate ID" wording. Comes from the doctor's organization — an
  // unemployed doctor has no ministry and gets the permissive wording.
  const isHealthMinistryDoctor = doctorInfo?.doctor_info?.ministry === Ministry.HEALTH;
  const proofOfPracticeKey = isHealthMinistryDoctor
    ? 'proof_of_practice_health_ministry'
    : 'proof_of_practice';

  // Webcam + cropper state
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [pendingCapture, setPendingCapture] = useState<PendingCapture | null>(null);
  const [cropperState, setCropperState] = useState<CropperState | null>(null);
  const hasLoadedData = useRef(false);

  // Load existing documents from DoctorInfoContext
  useEffect(() => {
    if (hasLoadedData.current || !doctorInfo) return;
    hasLoadedData.current = true;

    const loadExistingDocuments = async () => {
      try {
        setLoading(true);

        const info = doctorInfo.doctor_info || {};
        const documents = normalizeAttachments(info.documents || doctorInfo.documents);

        // نوع الطبيب as stored by the profile step, falling back to the old
        // which-id-is-filled inference for profiles saved before the column existed.
        setDoctorType(deriveDoctorType(info));

        // Graduated abroad ("Other"): load any previously uploaded equivalency cert
        // (visibility is derived from the profile via isOtherUniversity above).
        if (documents.certificate_equivalency) {
          setEquivalencyCert({
            file: null,
            preview: documents.certificate_equivalency,
            uploading: false,
            uploaded: true,
            error: null,
            existingUrl: documents.certificate_equivalency,
          });
        }

        // Load syndicate ID front if exists
        if (documents.syndicate_id_face) {
          setSyndicateIdFront({
            file: null,
            preview: documents.syndicate_id_face,
            uploading: false,
            uploaded: true,
            error: null,
            existingUrl: documents.syndicate_id_face,
          });
        }

        // Load syndicate ID back if exists
        if (documents.syndicate_id_back) {
          setSyndicateIdBack({
            file: null,
            preview: documents.syndicate_id_back,
            uploading: false,
            uploaded: true,
            error: null,
            existingUrl: documents.syndicate_id_back,
          });
        }

        // Higher-diploma administrative order (only asked of دبلوم عالي doctors)
        if (documents.higher_diploma_order) {
          setHigherDiplomaOrder({
            file: null,
            preview: documents.higher_diploma_order,
            uploading: false,
            uploaded: true,
            error: null,
            existingUrl: documents.higher_diploma_order,
          });
        }

        // Load specialization certificate if exists
        if (documents.specialization_certificate) {
          setSpecializationCert({
            file: null,
            preview: documents.specialization_certificate,
            uploading: false,
            uploaded: true,
            error: null,
            existingUrl: documents.specialization_certificate,
          });
        }

        // Load proof of practice if exists
        if (documents.proof_of_practice) {
          setProofOfPractice({
            file: null,
            preview: documents.proof_of_practice,
            uploading: false,
            uploaded: true,
            error: null,
            existingUrl: documents.proof_of_practice,
          });
        }

        // Load QR-fallback identity documents if present
        const loadDoc = (url: string | undefined, setter: DocSetter) => {
          if (url) {
            setter({ file: null, preview: url, uploading: false, uploaded: true, error: null, existingUrl: url });
          }
        };
        loadDoc(documents.residence_card_face, setResidenceCardFront);
        loadDoc(documents.residence_card_back, setResidenceCardBack);
        loadDoc(documents.profile_photo, setDoctorPhoto);
        loadDoc(documents.civil_id_face, setCivilIdFront);
        loadDoc(documents.civil_id_back, setCivilIdBack);
        loadDoc(documents.graduation_certificate, setGraduationCert);

        // Load other attachment slots — prefer per-slot types, fall back to legacy array.
        const legacyOther = documents.other_attachments
          ? (Array.isArray(documents.other_attachments) ? documents.other_attachments : [documents.other_attachments])
          : [];
        const slot1Url = documents.other_attachment_1 || legacyOther[0];
        const slot2Url = documents.other_attachment_2 || legacyOther[1];
        const otherSlots = [slot1Url, slot2Url]
          .filter(Boolean)
          .map((url: string) => ({
            file: null,
            preview: url,
            uploaded: true,
            uploading: false,
            existingUrl: url,
          }));
        if (otherSlots.length > 0) {
          setOtherAttachments(otherSlots);
        }
      } catch (error) {
        console.error('Failed to load existing documents:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingDocuments();
  }, [doctorInfo]);

  // Apply a final (post-crop or PDF) file to the doc state
  const applyFileToDoc = (file: File, setter: DocSetter) => {
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setter({
      file,
      preview,
      uploading: false,
      uploaded: false,
      error: null,
    });
  };

  // Handle file selection (gallery / file picker)
  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: DocSetter,
    aspect: number | undefined,
    inputId: string,
    fileNameBase: string
  ) => {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file still fires onChange (needed for retake)
    event.target.value = '';
    if (!file) return;

    const problem = checkUploadFile(file, { allowPdf: true });
    if (problem) {
      onError(t(uploadProblemKey(problem, true)));
      return;
    }

    // PDFs skip the crop step; so do HEIC/HEIF photos, which most browsers cannot draw.
    if (isPdfFile(file) || isHeic(file)) {
      applyFileToDoc(file, setter);
      return;
    }

    // Big docs (no fixed aspect) skip cropping — use the full image.
    if (!aspect) {
      applyFileToDoc(file, setter);
      return;
    }

    // Open the cropper for image files
    setPendingCapture({ setter, aspect, fileNameBase, inputId });
    setCropperState({
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || `${fileNameBase}_${Date.now()}.jpg`,
      aspect,
      source: 'file',
    });
  };

  // Webcam capture → open cropper, OR for big docs (no fixed aspect) use the full
  // image as-is so the whole document is captured (no cropping rectangle).
  const handleWebcamCapture = (file: File) => {
    if (!pendingCapture) return;
    if (!pendingCapture.aspect) {
      applyFileToDoc(file, pendingCapture.setter);
      setPendingCapture(null);
      return;
    }
    setCropperState({
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || `${pendingCapture.fileNameBase}_${Date.now()}.jpg`,
      aspect: pendingCapture.aspect,
      source: 'camera',
    });
  };

  const openWebcam = (
    setter: DocSetter,
    aspect: number | undefined,
    inputId: string,
    fileNameBase: string,
    allowFlip = false
  ) => {
    setPendingCapture({ setter, aspect, fileNameBase, inputId, allowFlip });
    setWebcamOpen(true);
  };

  // Cropper handlers
  const closeCropperAndCleanup = () => {
    if (cropperState?.imageSrc.startsWith('blob:')) {
      URL.revokeObjectURL(cropperState.imageSrc);
    }
    setCropperState(null);
  };

  const handleCropConfirm = (file: File) => {
    if (pendingCapture) {
      applyFileToDoc(file, pendingCapture.setter);
    }
    closeCropperAndCleanup();
    setPendingCapture(null);
  };

  const handleCropRetake = () => {
    const source = cropperState?.source;
    const ctx = pendingCapture;
    closeCropperAndCleanup();

    if (!ctx) return;

    if (source === 'camera') {
      setWebcamOpen(true);
    } else {
      // Re-trigger the file input
      const input = document.getElementById(`${ctx.inputId}-file`) as HTMLInputElement | null;
      input?.click();
    }
  };

  const handleCropCancel = () => {
    closeCropperAndCleanup();
    setPendingCapture(null);
  };

  // Handle file removal
  const handleFileRemove = (
    setter: React.Dispatch<React.SetStateAction<DocumentFile>>,
    preview: string | null
  ) => {
    // Only revoke blob URLs, not HTTP URLs from API
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setter({
      file: null,
      preview: null,
      uploading: false,
      uploaded: false,
      error: null,
    });
  };

  // Upload a single document
  const uploadDocument = async (
    doc: DocumentFile,
    setter: React.Dispatch<React.SetStateAction<DocumentFile>>,
    documentType: string
  ): Promise<boolean> => {
    // Skip if already uploaded (has existingUrl) or no new file to upload
    if (doc.uploaded && (doc.existingUrl || !doc.file)) return true;
    if (!doc.file) return false;

    setter(prev => ({ ...prev, uploading: true, error: null }));

    try {
      const result = await registrationApi.uploadDoctorDocument(doc.file, documentType);

      if (!result.isSuccess) {
        setter(prev => ({ ...prev, uploading: false, error: result.error || t('upload_failed') }));
        return false;
      }

      setter(prev => ({ ...prev, uploading: false, uploaded: true }));
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setter(prev => ({ ...prev, uploading: false, error: errorMessage }));
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate all documents are selected or already uploaded
    if (!syndicateIdFront.file && !syndicateIdFront.existingUrl) {
      onError(t('please_upload_syndicate_id_front'));
      return;
    }
    if (!syndicateIdBack.file && !syndicateIdBack.existingUrl) {
      onError(t('please_upload_syndicate_id_back'));
      return;
    }

    // Which professional document is required follows نوع الطبيب.
    if (isPractitioner) {
      if (!proofOfPractice.file && !proofOfPractice.existingUrl) {
        onError(t(`please_upload_${proofOfPracticeKey}`));
        return;
      }
    } else if (isHigherDiploma) {
      if (!higherDiplomaOrder.file && !higherDiplomaOrder.existingUrl) {
        onError(t('please_upload_higher_diploma_order'));
        return;
      }
    } else {
      if (!specializationCert.file && !specializationCert.existingUrl) {
        onError(t('please_upload_specialization_certificate'));
        return;
      }
    }

    // Graduated abroad ("Other"): require the Iraqi certificate-equivalency form
    if (isOtherUniversity && !equivalencyCert.file && !equivalencyCert.existingUrl) {
      onError(t('please_upload_certificate_equivalency'));
      return;
    }

    // QR-fallback doctors: require the extra identity documents.
    if (isQrDoctor) {
      const need: Array<[DocumentFile, string]> = [
        [residenceCardFront, 'please_upload_residence_card_front'],
        [residenceCardBack, 'please_upload_residence_card_back'],
        [doctorPhoto, 'please_upload_doctor_photo'],
        [civilIdFront, 'please_upload_civil_id_front'],
        [civilIdBack, 'please_upload_civil_id_back'],
        [graduationCert, 'please_upload_graduation_certificate'],
      ];
      for (const [doc, key] of need) {
        if (!doc.file && !doc.existingUrl) {
          onError(t(key));
          return;
        }
      }
    }

    setSubmitting(true);

    try {
      // Upload all documents based on doctor type
      const uploadPromises = [
        uploadDocument(syndicateIdFront, setSyndicateIdFront, 'syndicate_id_face'),
        uploadDocument(syndicateIdBack, setSyndicateIdBack, 'syndicate_id_back'),
      ];

      // The one professional document this نوع الطبيب owes
      if (isPractitioner) {
        uploadPromises.push(uploadDocument(proofOfPractice, setProofOfPractice, 'proof_of_practice'));
      } else if (isHigherDiploma) {
        uploadPromises.push(uploadDocument(higherDiplomaOrder, setHigherDiplomaOrder, 'higher_diploma_order'));
      } else {
        uploadPromises.push(uploadDocument(specializationCert, setSpecializationCert, 'specialization_certificate'));
      }

      // Equivalency certificate for degrees obtained abroad
      if (isOtherUniversity) {
        uploadPromises.push(uploadDocument(equivalencyCert, setEquivalencyCert, 'certificate_equivalency'));
      }

      // QR-fallback identity documents
      if (isQrDoctor) {
        uploadPromises.push(
          uploadDocument(residenceCardFront, setResidenceCardFront, 'residence_card_face'),
          uploadDocument(residenceCardBack, setResidenceCardBack, 'residence_card_back'),
          uploadDocument(doctorPhoto, setDoctorPhoto, 'profile_photo'),
          uploadDocument(civilIdFront, setCivilIdFront, 'civil_id_face'),
          uploadDocument(civilIdBack, setCivilIdBack, 'civil_id_back'),
          uploadDocument(graduationCert, setGraduationCert, 'graduation_certificate'),
        );
      }

      const results = await Promise.all(uploadPromises);

      // Upload other_attachments
      let otherSuccess = true;
      for (let index = 0; index < otherAttachments.length; index++) {
        const att = otherAttachments[index];
        if (att.file) {
          setOtherAttachments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], uploading: true };
            return updated;
          });

          const slotType = index === 0 ? 'other_attachment_1' : 'other_attachment_2';
          const result = await registrationApi.uploadDoctorDocument(att.file, slotType);

          setOtherAttachments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], uploading: false, uploaded: result.isSuccess };
            return updated;
          });

          if (!result.isSuccess) otherSuccess = false;
        }
      }

      // Check if all uploads succeeded
      if (results.every(success => success) && otherSuccess) {
        // Navigate directly to home, skip redirect check on destination
        skipNextStepRedirect();
        navigate('/home');
      } else {
        onError(t('some_documents_failed_to_upload'));
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : t('failed_to_upload_documents'));
    } finally {
      setSubmitting(false);
    }
  };

  // Cleanup previews on unmount (only blob URLs, not HTTP URLs)
  useEffect(() => {
    return () => {
      // Only revoke blob URLs (starts with 'blob:'), not HTTP URLs from API
      if (syndicateIdFront.preview && syndicateIdFront.preview.startsWith('blob:')) {
        URL.revokeObjectURL(syndicateIdFront.preview);
      }
      if (syndicateIdBack.preview && syndicateIdBack.preview.startsWith('blob:')) {
        URL.revokeObjectURL(syndicateIdBack.preview);
      }
      if (specializationCert.preview && specializationCert.preview.startsWith('blob:')) {
        URL.revokeObjectURL(specializationCert.preview);
      }
      if (proofOfPractice.preview && proofOfPractice.preview.startsWith('blob:')) {
        URL.revokeObjectURL(proofOfPractice.preview);
      }
    };
  }, [syndicateIdFront.preview, syndicateIdBack.preview, specializationCert.preview, proofOfPractice.preview]);

  const renderFileUpload = (
    label: string,
    doc: DocumentFile,
    setter: DocSetter,
    inputId: string,
    aspect: number | undefined,
    fileNameBase: string,
    allowFlip = false
  ) => (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2 break-words">
        {label} <span className="text-red-500">*</span>
      </label>

      {!doc.file && !doc.preview ? (
        <div>
          <div className="flex items-center gap-3 mb-2">
            {/* Take Photo Button (Camera) */}
            <button
              onClick={() => openWebcam(setter, aspect, inputId, fileNameBase, allowFlip)}
              disabled={submitting}
              type="button"
              className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-4xl mb-2">📷</div>
              <span className="text-sm font-medium text-gray-700">{t('take_photo') || 'Take Photo'}</span>
            </button>

            {/* Or Divider */}
            <span className="text-sm text-gray-500 font-medium">{t('or') || 'Or'}</span>

            {/* Choose File Button */}
            <label
              htmlFor={`${inputId}-file`}
              className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <FaUpload className="text-3xl text-gray-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('choose_from_files') || 'Choose from Files'}</span>
              <input
                id={`${inputId}-file`}
                type="file"
                className="hidden"
                accept={ACCEPT_IMAGES_AND_PDF}
                onChange={(e) => handleFileSelect(e, setter, aspect, inputId, fileNameBase)}
                disabled={submitting}
              />
            </label>
          </div>

          <p className="text-xs text-center text-gray-400">{t('max_10mb_image_or_pdf')}</p>
        </div>
      ) : (
        <div className="relative border-2 border-green-300 rounded-lg p-4 bg-green-50">
          {doc.preview ? (
            <img src={doc.preview} alt={label} className="w-full h-32 object-contain mb-2" />
          ) : (
            <div className="flex items-center justify-center h-32 bg-gray-100 mb-2">
              <span className="text-sm text-gray-600">{t('pdf_file')}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700 truncate flex-1">
              {doc.file ? doc.file.name : (doc.existingUrl ? t('uploaded_document') || 'Uploaded Document' : '')}
            </span>

            {doc.uploading && (
              <div className="ml-2">
                <CooperationLoader />
              </div>
            )}

            {doc.uploaded && (
              <FaCircleCheck className="text-green-500 text-xl ml-2" />
            )}

            {!doc.uploading && !submitting && (
              <button
                onClick={() => handleFileRemove(setter, doc.preview)}
                className="ml-2 text-red-500 hover:text-red-700"
                type="button"
              >
                <FaTrash />
              </button>
            )}
          </div>

          {doc.error && (
            <p className="text-red-500 text-sm mt-2">{doc.error}</p>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <CooperationLoader heightClass="h-16 sm:h-20" />
      </div>
    );
  }

  return (
    <ResponsiveFormLayout
      onBack={onBack}
      showLanguageToggle={true}
      icon={upload_icon}
      iconSize="w-24 h-24"
      title={t('upload_documents') || 'Upload Documents'}
      subtitle={t('upload_required_documents') || 'Please upload the required documents'}
      maxHeight="90vh"
      actions={
        <div className="flex gap-3 w-full">
          <button
            onClick={onBack}
            disabled={submitting}
            className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
          >
            {t('back') || 'Back'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !syndicateIdFront.file && !syndicateIdFront.existingUrl ||
              !syndicateIdBack.file && !syndicateIdBack.existingUrl ||
              (isPractitioner
                ? (!proofOfPractice.file && !proofOfPractice.existingUrl)
                : isHigherDiploma
                ? (!higherDiplomaOrder.file && !higherDiplomaOrder.existingUrl)
                : (!specializationCert.file && !specializationCert.existingUrl))
            }
            className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
              submitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
            }`}
            style={{ backgroundColor: submitting ? '#B0B0B0' : '#2E5D7D' }}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>{t('uploading') || 'Uploading...'}</span>
              </>
            ) : (
              <span>{t('submit_documents') || 'Submit Documents'}</span>
            )}
          </button>
        </div>
      }
    >
      <div className="w-full">
        {renderFileUpload(
          t('syndicate_id_front') || 'Syndicate ID (Front)',
          syndicateIdFront,
          setSyndicateIdFront,
          'syndicate-front',
          ID_CARD_ASPECT,
          'syndicate_id_face'
        )}

        {renderFileUpload(
          t('syndicate_id_back') || 'Syndicate ID (Back)',
          syndicateIdBack,
          setSyndicateIdBack,
          'syndicate-back',
          ID_CARD_ASPECT,
          'syndicate_id_back'
        )}

        {/* One professional document, chosen by نوع الطبيب. */}
        {isPractitioner &&
          renderFileUpload(
            t(proofOfPracticeKey) || 'Proof of Practice',
            proofOfPractice,
            setProofOfPractice,
            'proof-of-practice',
            undefined, // big document → full camera, no crop rectangle
            'proof_of_practice'
          )}

        {isHigherDiploma &&
          renderFileUpload(
            t('higher_diploma_order') || 'Higher Diploma Administrative Order',
            higherDiplomaOrder,
            setHigherDiplomaOrder,
            'higher-diploma-order',
            undefined, // big document → full camera, no crop rectangle
            'higher_diploma_order'
          )}

        {needsSpecializationCert &&
          renderFileUpload(
            t('specialization_certificate') || 'Specialization Certificate',
            specializationCert,
            setSpecializationCert,
            'specialization-cert',
            undefined, // big document → full camera, no crop rectangle
            'specialization_certificate'
          )}

        {/* Certificate equivalency form — required for degrees obtained abroad ("Other" university) */}
        {isOtherUniversity &&
          renderFileUpload(
            t('certificate_equivalency') || 'Certificate Equivalency Form',
            equivalencyCert,
            setEquivalencyCert,
            'certificate-equivalency',
            undefined, // big document → full camera, no crop rectangle
            'certificate_equivalency'
          )}

        {/* QR-fallback identity documents (residence card, doctor photo, civil/unified ID) */}
        {isQrDoctor && (
          <>
            {renderFileUpload(
              t('residence_card_front') || 'Residence Card (Front)',
              residenceCardFront, setResidenceCardFront, 'residence-front', ID_CARD_ASPECT, 'residence_card_face'
            )}
            {renderFileUpload(
              t('residence_card_back') || 'Residence Card (Back)',
              residenceCardBack, setResidenceCardBack, 'residence-back', ID_CARD_ASPECT, 'residence_card_back'
            )}
            {renderFileUpload(
              t('doctor_photo') || 'Doctor Photo',
              doctorPhoto, setDoctorPhoto, 'doctor-photo', 0.8, 'profile_photo', true /* allow flip camera */
            )}
            {renderFileUpload(
              t('civil_id_front') || 'Civil / Unified ID (Front)',
              civilIdFront, setCivilIdFront, 'civil-front', ID_CARD_ASPECT, 'civil_id_face'
            )}
            {renderFileUpload(
              t('civil_id_back') || 'Civil / Unified ID (Back)',
              civilIdBack, setCivilIdBack, 'civil-back', ID_CARD_ASPECT, 'civil_id_back'
            )}
            {renderFileUpload(
              t('graduation_certificate') || 'Graduation Certificate',
              graduationCert, setGraduationCert, 'graduation-cert', undefined /* full camera, no crop */, 'graduation_certificate'
            )}
          </>
        )}

        {/* Other Attachments (optional, max 2) */}
        <ClinicRoomsUpload
          rooms={otherAttachments}
          onRoomsChange={setOtherAttachments}
          onError={onError}
          loading={submitting}
          title={t('other_attachments') || 'Other Attachments'}
          required={false}
          maxCount={2}
          itemLabel={t('attachment') || 'Attachment'}
        />

      </div>

      {/* Webcam Modal */}
      {webcamOpen && (
        <WebcamCapture
          onCapture={(file) => {
            setWebcamOpen(false);
            handleWebcamCapture(file);
          }}
          onClose={() => setWebcamOpen(false)}
          showGuideFrame={!!pendingCapture?.aspect}
          guideAspectRatio={pendingCapture?.aspect}
          allowFlip={!!pendingCapture?.allowFlip}
        />
      )}

      {/* Crop Modal */}
      {cropperState && (
        <ImageCropper
          imageSrc={cropperState.imageSrc}
          fileName={cropperState.fileName}
          aspect={cropperState.aspect}
          onConfirm={handleCropConfirm}
          onRetake={handleCropRetake}
          onCancel={handleCropCancel}
        />
      )}
    </ResponsiveFormLayout>
  );
};

export default DoctorDocumentsScreen;
